import React, { useState, useEffect, useCallback } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { fetchWithAuth } from '../../lib/api/fetchWithAuth';
import { debounce } from 'lodash';

interface OnboardingFlowProps {
  onComplete: () => void;
}

type OnboardingStep = 'choose' | 'create-org' | 'create-brand' | 'join';

type SubdomainStatus = 'checking' | 'available' | 'taken' | 'invalid' | null;

interface Organization {
  id: string;
  name: string;
  description?: string;
  slug: string;
  logo?: string | null;
  brandColor?: string | null;
  createdAt: string;
  updatedAt: string;
  role: string;
}

interface Brand {
  id: string;
  name: string;
  description?: string;
  slug: string;
  subdomain: string;
  organizationId: string;
  createdAt: string;
  updatedAt: string;
}

export function OnboardingFlow({ onComplete }: OnboardingFlowProps) {
  const { getAccessTokenSilently } = useAuth0();
  const [step, setStep] = useState<OnboardingStep>('choose');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [subdomainStatus, setSubdomainStatus] = useState<SubdomainStatus>(null);
  
  // Form states
  const [orgName, setOrgName] = useState('');
  const [orgDescription, setOrgDescription] = useState('');
  const [brandName, setBrandName] = useState('');
  const [brandDescription, setBrandDescription] = useState('');
  const [brandSubdomain, setBrandSubdomain] = useState('');
  const [inviteCode, setInviteCode] = useState('');
  const [createdOrg, setCreatedOrg] = useState<Organization | null>(null);

  // Debounced subdomain check
  const checkSubdomainAvailability = useCallback(
    debounce(async (subdomain: string) => {
      if (subdomain.length < 3) {
        setSubdomainStatus('invalid');
        return;
      }

      try {
        setSubdomainStatus('checking');
        const token = await getAccessTokenSilently();
        const response = await fetchWithAuth<{ available: boolean }>(`/api/organizations/temp/brands/check-subdomain/${subdomain}`, {
          method: 'GET',
          token
        });
        setSubdomainStatus(response?.available ? 'available' : 'taken');
      } catch (err) {
        console.error('Error checking subdomain:', err);
        setSubdomainStatus(null);
      }
    }, 500),
    [getAccessTokenSilently]
  );

  // Check subdomain availability when it changes
  useEffect(() => {
    if (brandSubdomain) {
      if (brandSubdomain.length < 3) {
        setSubdomainStatus('invalid');
      } else {
        checkSubdomainAvailability(brandSubdomain);
      }
    } else {
      setSubdomainStatus(null);
    }

    // Cleanup
    return () => {
      checkSubdomainAvailability.cancel();
    };
  }, [brandSubdomain, checkSubdomainAvailability]);

  const getSubdomainStatusColor = () => {
    switch (subdomainStatus) {
      case 'available':
        return 'text-green-400';
      case 'taken':
        return 'text-red-400';
      case 'checking':
        return 'text-yellow-400';
      case 'invalid':
        return 'text-red-400';
      default:
        return 'text-gray-400';
    }
  };

  const getSubdomainStatusText = () => {
    switch (subdomainStatus) {
      case 'available':
        return 'Available!';
      case 'taken':
        return 'Already taken';
      case 'checking':
        return 'Checking availability...';
      case 'invalid':
        return 'Must be at least 3 characters';
      default:
        return '';
    }
  };

  const handleCreateOrg = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsSubmitting(true);

    try {
      const token = await getAccessTokenSilently();
      
      // Create organization
      const response = await fetchWithAuth<Organization>('/api/organizations', {
        method: 'POST',
        token,
        body: JSON.stringify({ 
          name: orgName,
          description: orgDescription 
        })
      });

      console.log('Organization response:', response);
      
      // Store the organization data (response is already unwrapped)
      setCreatedOrg(response);
      
      setStep('create-brand');
    } catch (err) {
      console.error('Error creating organization:', err);
      setError(err instanceof Error ? err.message : 'Failed to create organization');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCreateBrand = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (subdomainStatus !== 'available') {
      setError('Please choose an available subdomain');
      return;
    }

    setIsSubmitting(true);

    console.log('Creating brand with org:', createdOrg);

    if (!createdOrg?.id) {
      console.error('No organization ID found:', createdOrg);
      setError('No organization found. Please try again.');
      setIsSubmitting(false);
      return;
    }

    try {
      const token = await getAccessTokenSilently();
      
      console.log('Creating brand for organization:', createdOrg.id);
      
      // Create brand in the new organization
      const response = await fetchWithAuth<Brand>(`/api/organizations/${createdOrg.id}/brands`, {
        method: 'POST',
        token,
        body: JSON.stringify({ 
          name: brandName,
          description: brandDescription,
          subdomain: brandSubdomain
        })
      });

      console.log('Created brand:', response);
      onComplete();
    } catch (err) {
      console.error('Error creating brand:', err);
      setError(err instanceof Error ? err.message : 'Failed to create brand');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleJoinOrg = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsSubmitting(true);

    try {
      const token = await getAccessTokenSilently();
      await fetchWithAuth('/auth/invite/' + inviteCode + '/redeem', {
        method: 'POST',
        token
      });
      onComplete();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to join organization');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-900 flex items-center justify-center px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        {step === 'choose' && (
          <div className="text-center">
            <h2 className="mt-6 text-3xl font-extrabold text-gray-100">
              Welcome! Let's get started
            </h2>
            <p className="mt-2 text-sm text-gray-400">
              Choose how you'd like to begin
            </p>
            <div className="mt-8 space-y-4">
              <button
                onClick={() => setStep('create-org')}
                className="w-full flex justify-center py-3 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              >
                Create a new organization
              </button>
              <button
                onClick={() => setStep('join')}
                className="w-full flex justify-center py-3 px-4 border border-gray-700 rounded-md shadow-sm text-sm font-medium text-gray-300 bg-gray-800 hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              >
                Join an existing organization
              </button>
            </div>
          </div>
        )}

        {step === 'create-org' && (
          <div>
            <h2 className="mt-6 text-3xl font-extrabold text-gray-100">
              Create your organization
            </h2>
            <p className="mt-2 text-sm text-gray-400">
              Set up your organization details
            </p>
            <form onSubmit={handleCreateOrg} className="mt-8 space-y-6">
              <div className="space-y-4">
                <div>
                  <label htmlFor="orgName" className="block text-sm font-medium text-gray-300">
                    Organization Name
                  </label>
                  <input
                    id="orgName"
                    type="text"
                    required
                    value={orgName}
                    onChange={(e) => setOrgName(e.target.value)}
                    className="mt-1 block w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-gray-100 placeholder-gray-400 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                    placeholder="Your Organization"
                  />
                </div>
                <div>
                  <label htmlFor="orgDescription" className="block text-sm font-medium text-gray-300">
                    Description
                  </label>
                  <textarea
                    id="orgDescription"
                    value={orgDescription}
                    onChange={(e) => setOrgDescription(e.target.value)}
                    className="mt-1 block w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-gray-100 placeholder-gray-400 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                    placeholder="Describe your organization"
                    rows={3}
                  />
                </div>
              </div>

              {error && (
                <div className="text-red-400 text-sm">
                  {error}
                </div>
              )}

              <div className="flex items-center justify-between">
                <button
                  type="button"
                  onClick={() => setStep('choose')}
                  className="text-sm text-gray-400 hover:text-gray-300"
                >
                  Back
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting || !orgName.trim()}
                  className="flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? 'Creating...' : 'Next'}
                </button>
              </div>
            </form>
          </div>
        )}

        {step === 'create-brand' && (
          <div>
            <h2 className="mt-6 text-3xl font-extrabold text-gray-100">
              Create your first brand
            </h2>
            <p className="mt-2 text-sm text-gray-400">
              Set up your brand details
            </p>
            <form onSubmit={handleCreateBrand} className="mt-8 space-y-6">
              <div className="space-y-4">
                <div>
                  <label htmlFor="brandName" className="block text-sm font-medium text-gray-300">
                    Brand Name
                  </label>
                  <input
                    id="brandName"
                    type="text"
                    required
                    value={brandName}
                    onChange={(e) => setBrandName(e.target.value)}
                    className="mt-1 block w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-gray-100 placeholder-gray-400 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                    placeholder="Your Brand"
                  />
                </div>
                <div>
                  <label htmlFor="brandDescription" className="block text-sm font-medium text-gray-300">
                    Description
                  </label>
                  <textarea
                    id="brandDescription"
                    value={brandDescription}
                    onChange={(e) => setBrandDescription(e.target.value)}
                    className="mt-1 block w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-gray-100 placeholder-gray-400 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                    placeholder="Describe your brand"
                    rows={3}
                  />
                </div>
                <div>
                  <label htmlFor="brandSubdomain" className="block text-sm font-medium text-gray-300">
                    Subdomain
                  </label>
                  <div className="mt-1 flex rounded-md shadow-sm">
                    <input
                      type="text"
                      id="brandSubdomain"
                      required
                      value={brandSubdomain}
                      onChange={(e) => setBrandSubdomain(e.target.value)}
                      className="flex-1 min-w-0 block w-full px-3 py-2 rounded-l-md bg-gray-700 border border-r-0 border-gray-600 text-gray-100 placeholder-gray-400 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                      placeholder="yourbrand"
                    />
                    <span className="inline-flex items-center px-3 rounded-r-md border border-l-0 border-gray-600 bg-gray-800 text-gray-400">
                      .creatorform.app
                    </span>
                  </div>
                  {brandSubdomain.length > 0 && (
                    <p className={`mt-1 text-sm ${getSubdomainStatusColor()}`}>
                      {getSubdomainStatusText()}
                    </p>
                  )}
                </div>
              </div>

              {error && (
                <div className="text-red-400 text-sm">
                  {error}
                </div>
              )}

              <div className="flex items-center justify-between">
                <button
                  type="button"
                  onClick={() => setStep('create-org')}
                  className="text-sm text-gray-400 hover:text-gray-300"
                >
                  Back
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting || !brandName.trim() || !brandSubdomain.trim()}
                  className="flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? 'Creating...' : 'Create Brand'}
                </button>
              </div>
            </form>
          </div>
        )}

        {step === 'join' && (
          <div>
            <h2 className="mt-6 text-3xl font-extrabold text-gray-100">
              Join an organization
            </h2>
            <p className="mt-2 text-sm text-gray-400">
              Enter your invite code to join an existing organization
            </p>
            <form onSubmit={handleJoinOrg} className="mt-8 space-y-6">
              <div>
                <label htmlFor="inviteCode" className="block text-sm font-medium text-gray-300">
                  Invite Code
                </label>
                <input
                  id="inviteCode"
                  type="text"
                  required
                  value={inviteCode}
                  onChange={(e) => setInviteCode(e.target.value)}
                  className="mt-1 block w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-gray-100 placeholder-gray-400 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                  placeholder="Enter your invite code"
                />
              </div>

              {error && (
                <div className="text-red-400 text-sm">
                  {error}
                </div>
              )}

              <div className="flex items-center justify-between">
                <button
                  type="button"
                  onClick={() => setStep('choose')}
                  className="text-sm text-gray-400 hover:text-gray-300"
                >
                  Back
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting || !inviteCode.trim()}
                  className="flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? 'Joining...' : 'Join'}
                </button>
              </div>
            </form>
          </div>
        )}
      </div>
    </div>
  );
} 